import { type IUserModulesData } from "@interfaces/modules/IGetModulesResponse";
import { OnboardingProgressService } from "@modules/onboarding/services/onboarding-progress.service";
import { Store } from "@ngxs/store";
import { AuthService } from "@services/auth.service";
import { ModulesService } from "@services/modules.service";
import { ToastService } from "@services/toast.service";
import {
  SetAccessibleModules,
  SetCurrentTenant,
  SetPlanStatus,
} from "@state/common/common.action";
import { UserState } from "@state/user/user.state";
import { parseApiError } from "@utils/common.utils";
import { catchError, from, map, of, switchMap } from "rxjs";

import { HttpErrorResponse } from "@angular/common/http";
import { inject } from "@angular/core";
import { type CanActivateFn, Router } from "@angular/router";

export const dashboardAccessGuard: CanActivateFn = route => {
  const authService = inject(AuthService);
  const modulesService = inject(ModulesService);
  const onboardingProgressService = inject(OnboardingProgressService);
  const router = inject(Router);
  const store = inject(Store);
  const toastService = inject(ToastService);

  return from(authService.ensureValidSession()).pipe(
    switchMap(isSessionValid => {
      if (!isSessionValid) return of(router.parseUrl("/login"));

      const tenant = route.paramMap.get("tenantId");
      const tenants = store.selectSnapshot(UserState.tenants);
      const isPlatformAdministrator = store.selectSnapshot(
        UserState.isPlatformAdministrator
      );
      if (
        !tenant ||
        (!isPlatformAdministrator &&
          !tenants.some(item => item.tenantName === tenant))
      ) {
        return of(router.parseUrl("/no-access"));
      }

      return onboardingProgressService.getProgress(tenant).pipe(
        switchMap(onboardingProgress => {
          if (onboardingProgress.data.status !== "completed") {
            store.dispatch(new SetCurrentTenant(tenant));
            return of(router.parseUrl("/onboarding"));
          }

          return modulesService.getUserAccessibleModules(tenant).pipe(
            map(response => {
              const data: IUserModulesData = Array.isArray(response.data)
                ? {
                    planActive: true,
                    planName: null,
                    expirationDate: null,
                    modules: response.data,
                  }
                : response.data;

              store.dispatch([
                new SetCurrentTenant(tenant),
                new SetPlanStatus({
                  planActive: data.planActive,
                  planName: data.planName,
                  expirationDate: data.expirationDate,
                }),
              ]);

              if (!data.planActive) {
                return router.parseUrl("/plan-expired");
              }

              store.dispatch(new SetAccessibleModules(data.modules));
              return true;
            })
          );
        })
      );
    }),
    catchError(error => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        authService.endSession();
        return of(router.parseUrl("/login"));
      }
      if (error instanceof HttpErrorResponse && error.status === 403) {
        return of(router.parseUrl("/no-access"));
      }
      const { toastType, userMessage } = parseApiError(error);
      toastService.showCompactToast(toastType, userMessage);
      return of(false);
    })
  );
};
